import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { EVENT_SALES_COLOR, EVENT_SALES_LABEL } from '../lib/constants.js';

// True if event `e` spans the given YYYY-MM-DD (used only by this banner).
function eventOccursOn(e, ymd) {
  const s = String(e.start_date || '').slice(0, 10);
  const en = String(e.end_date || s).slice(0, 10);
  return ymd >= s && ymd <= en;
}

// ─── Events banner (Daily tab) ─────────────────────────────────────────────
// Compact strip showing today's + next-7-day events for the current
// location. Read-only. Visible to every authenticated user including
// hourly staff. Uses the public /api/events endpoint.
export function EventsBanner({ locationId, locationName, dateYmd }) {
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!locationId || !dateYmd) return;
    const start = new Date(dateYmd + 'T12:00:00');
    const end = new Date(start.getTime() + 7 * 86400000);
    const to = end.toISOString().slice(0, 10);
    const params = new URLSearchParams({ location_id: String(locationId), from: dateYmd, to });
    api('GET', `/api/events?${params.toString()}`)
      .then(r => setEvents(Array.isArray(r?.events) ? r.events : []))
      .catch(() => setEvents([]))
      .finally(() => setLoaded(true));
  }, [locationId, dateYmd]);
  if (!loaded || events.length === 0) return null;
  const today = events.filter(e => eventOccursOn(e, dateYmd));
  const upcoming = events.filter(e => !eventOccursOn(e, dateYmd)).slice(0, 4);
  function fmtPretty(ymd) {
    return new Date(ymd + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }
  return (
    <div style={{ background: '#fef9ee', border: '1px solid #f5e6c8', borderRadius: 10,
                    padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#8e6c33',
                      textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        Events {locationName ? `· ${locationName}` : ''}
      </div>
      {today.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {today.map(e => {
            const c = EVENT_SALES_COLOR[e.sales_impact] || EVENT_SALES_COLOR.unknown;
            return (
              <div key={`t-${e.id}-${e.start_date}`}
                style={{ display: 'flex', flexDirection: 'column', gap: 4,
                          background: '#fff', border: '1px solid #f0d9a3',
                          borderRadius: 8, padding: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>📍 Today: {e.title}</span>
                  <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                                    background: c.bg, color: c.fg, border: `1px solid ${c.bd}` }}>
                      {EVENT_SALES_LABEL[e.sales_impact] || e.sales_impact}
                    </span>
                    {e.staffing_restricted && (
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                                      background: '#fef9ee', color: '#8e6c33', border: '1px solid #f5e6c8' }}>
                        🔒 Restricted
                      </span>
                    )}
                  </span>
                </div>
                {e.notes && <div style={{ fontSize: 13, color: '#444' }}>{e.notes}</div>}
                {e.url && (
                  <a href={e.url} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 12, color: '#1976d2', textDecoration: 'none' }}>
                    🔗 More info →
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
      {upcoming.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {upcoming.map(e => (
            <div key={`u-${e.id}-${e.start_date}`}
              style={{ fontSize: 12, padding: '4px 10px', borderRadius: 99,
                        background: '#fff', border: '1px solid #e0d4a8', color: '#5a4220' }}>
              {fmtPretty(String(e.start_date).slice(0, 10))} · {e.title}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
